import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Instagram, Youtube, Mail } from "lucide-react";
import FoxMotif from "@/components/site/FoxMotif";
import ContactForm from "@/components/site/ContactForm";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2H21.5l-7.6 8.683L22.999 22h-6.84l-5.355-6.99L4.59 22H1.33l8.122-9.282L1.001 2h6.999l4.842 6.4L18.244 2Zm-1.197 18h1.871L7.04 4H5.06l11.987 16Z" />
  </svg>
);

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.5 3a5.5 5.5 0 0 0 5.5 5.5v3.25a8.7 8.7 0 0 1-5.5-1.92v6.92a6.25 6.25 0 1 1-6.25-6.25c.27 0 .53.02.79.06v3.31a3 3 0 1 0 2.21 2.88V3h3.25Z" />
  </svg>
);

const SOCIAL_META = {
  x: { label: "X (Twitter)", Icon: XIcon },
  instagram: { label: "Instagram", Icon: Instagram },
  tiktok: { label: "TikTok", Icon: TikTokIcon },
  youtube: { label: "YouTube", Icon: Youtube },
};

export const Footer = ({ content }) => {
  const c = content || {};
  const socials = c.socials || [];
  const navigate = useNavigate();
  const clicksRef = useRef([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSession, setContactSession] = useState(0);
  const year = new Date().getFullYear();

  const openContact = () => {
    setContactSession((s) => s + 1);
    setContactOpen(true);
  };

  // Easter egg: 5 clicks on the footer fox logo within 4 seconds opens /admin
  const handleLogoClick = () => {
    const now = Date.now();
    clicksRef.current = [...clicksRef.current.filter((t) => now - t < 4000), now];
    if (clicksRef.current.length >= 5) {
      clicksRef.current = [];
      navigate("/admin");
    }
  };

  return (
    <>
      <footer
        id="contact"
        data-testid="site-footer"
        className="relative bg-[#121316] text-[#FBFAF8] overflow-hidden"
      >
        <div className="pointer-events-none absolute inset-0 pf-charcoal-glow" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 pf-noise" aria-hidden="true" />

        <div className="pf-container relative pt-16 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleLogoClick}
                  aria-label="Play Fox Studio"
                  data-testid="footer-fox-easter-egg"
                  className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121316]"
                  style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <FoxMotif size={44} testId="footer-fox-motif" />
                </button>
                <span className="pf-display text-[20px]">
                  Play Fox <span className="text-[#F35A1F]">Studio</span>
                </span>
              </div>
              <p className="mt-4 max-w-sm text-white/70">{c.tagline}</p>
              <button
                type="button"
                onClick={openContact}
                data-testid="footer-contact-button"
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121316]"
              >
                <Mail className="h-4 w-4 text-[#F35A1F]" />
                Contact us
              </button>
              {c.email ? (
                <p className="mt-2 text-xs text-white/45">
                  or email <a className="underline" href={`mailto:${c.email}`} data-testid="footer-email-link">{c.email}</a>
                </p>
              ) : null}
            </div>

            <div className="md:col-span-3">
              <p className="pf-mono text-[11px] uppercase tracking-wider text-white/50">Explore</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                {[
                  { id: "apps", label: "Featured App" },
                  { id: "portfolio", label: "Portfolio" },
                  { id: "about", label: "About" },
                  { id: "home", label: "Top of page" },
                ].map((l) => (
                  <li key={l.id}>
                    <a
                      href={`#${l.id}`}
                      data-testid={`footer-link-${l.id}`}
                      className="text-white/75 hover:text-white pf-link-underline"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-4">
              <p className="pf-mono text-[11px] uppercase tracking-wider text-white/50">Follow along</p>
              {(() => {
                const visibleSocials = socials.filter((s) => s.visible === true);
                if (visibleSocials.length === 0) {
                  return (
                    <p
                      data-testid="footer-socials-empty"
                      className="mt-4 text-xs text-white/55"
                    >
                      Social channels are on the way. {c.follow_blurb}
                    </p>
                  );
                }
                return (
                  <>
                    <ul className="mt-4 flex flex-wrap gap-2">
                      {visibleSocials.map((s) => {
                        const meta = SOCIAL_META[s.id] || { label: s.id, Icon: XIcon };
                        const { Icon, label } = meta;
                        return (
                          <li key={s.id}>
                            <a
                              href={s.url || "#"}
                              aria-label={label}
                              data-testid={`footer-social-link-${s.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white/[0.10] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121316]"
                            >
                              <Icon width="18" height="18" className="text-white" />
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-5 text-xs text-white/55">{c.follow_blurb}</p>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="mt-12 flex flex-col items-start gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-white/55" data-testid="footer-copyright">
                © {year} Play Fox Studio. {c.copyright_text}
              </p>
              {c.abn ? (
                <p
                  data-testid="footer-abn"
                  className="mt-1 pf-mono text-[10px] uppercase tracking-wider text-white/45"
                >
                  ABN {c.abn}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              <Link
                to="/privacy"
                data-testid="footer-privacy-link"
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-white/75 hover:text-white pf-link-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121316]"
              >
                Privacy Policy
              </Link>
              <span className="text-white/30">·</span>
              <button
                type="button"
                onClick={openContact}
                data-testid="footer-bottom-contact"
                className="rounded-md px-2 py-1 text-xs text-white/75 hover:text-white pf-link-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121316]"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
      <ContactForm key={contactSession} open={contactOpen} onOpenChange={setContactOpen} />
    </>
  );
};

export default Footer;
